<script setup lang="ts">
import { ref, computed, watchEffect } from "vue";
import { useAuth } from "../lib/useAuth";
import Beacon from "@/lib/beacon";
import { useRfpRfis, useRfpQas } from "@/lib/useRfp";

import RfiDialog from "@/components/RfiDialog.vue";

const { isProvider } = useAuth();

const props = defineProps<{
  rfpId: string;
  enableQuestionButton: boolean;
}>();

const reactiveRfpId = computed(() => props.rfpId);
const { rfis, reloadRfis, loading: rfisLoading } = useRfpRfis(reactiveRfpId);

const { qas, loading: qasLoading } = useRfpQas(reactiveRfpId);

const currentQuestionId = ref<string | null>(null);
const currentRfiId = ref<string | null>(null);
const rfiDialogOpen = ref(false);

watchEffect(() => {
  if (currentQuestionId.value !== null) {
    Beacon.rfpQuestionView(props.rfpId, currentQuestionId.value);
  }
});

watchEffect(() => {
  if (currentRfiId.value !== null) {
    Beacon.rfiView(props.rfpId, currentRfiId.value);
  }
});

function handleClickRfi(newRfiId: string) {
  if (currentRfiId.value === newRfiId) {
    currentRfiId.value = null;
  } else {
    currentRfiId.value = newRfiId;
  }
}

function handleClickQA(newQAId: string) {
  if (currentQuestionId.value === newQAId) {
    currentQuestionId.value = null;
  } else {
    currentQuestionId.value = newQAId;
  }
}
</script>
<template>
  <Skeleton v-if="rfisLoading || qasLoading" height="24em" />
  <div v-else>
    <template v-if="isProvider && rfis">
      <div>
        <p class="tw:font-semibold tw:text-base tw:text-zinc-400">
          Submit your questions
        </p>
        <span v-if="rfis.length === 0" class="tw:text-zinc-500">
          Your organization has not submitted any questions.
        </span>
        <span class="tw:text-zinc-500">
          Click the "submit new question" button below to ask a question.
        </span>
      </div>
      <Accordion
        v-if="rfis.length"
        class="tw:my-4"
        @update:value="handleClickRfi($event as string)"
      >
        <AccordionPanel
          v-for="rfi in rfis"
          :key="rfi.id"
          :value="rfi.id"
          :disabled="!rfi.answer"
        >
          <AccordionHeader>
            <div class="tw:flex tw:flex-col tw:items-start tw:gap-2">
              <div
                class="tw:flex tw:max-sm:flex-col tw:justify-between tw:w-full tw:items-start tw:max-sm:gap-2 tw:gap-4"
              >
                <div class="tw:text-base" v-html="rfi.question" />

                <TChip small v-if="rfi.archived" dense color="gray">
                  <TIcon small left>inventory</TIcon>
                  Archived
                </TChip>
                <TChip small v-else-if="!rfi.answer" dense color="orange">
                  <TIcon small left>pending_actions</TIcon>
                  Pending
                </TChip>
                <TChip small v-else dense color="green">
                  <TIcon small left>done</TIcon>
                  Answered
                </TChip>
              </div>
              <TChip dense disabled class="tw:p-0!">
                <TIcon small left>person</TIcon>
                Asked by {{ rfi.name }}
              </TChip>
            </div>
          </AccordionHeader>
          <AccordionContent>
            <div class="tw:pt-2">
              <div
                v-html="rfi.answer"
                class="tw:text-base tw:border-l-4 tw:border-zinc-500/50 tw:px-4"
              />
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
      <div class="tw:flex tw:justify-start tw:py-4">
        <RfiDialog
          :disabled="!enableQuestionButton"
          v-model="rfiDialogOpen"
          :rfp-id="rfpId"
          @close="reloadRfis"
        />
      </div>
    </template>
    <div class="tw:pt-4">
      <p class="tw:font-semibold tw:text-base tw:text-zinc-400">
        Review all questions and answers
      </p>
      <span v-if="qas && qas.length === 0" class="tw:text-zinc-500">
        There are no questions and answers to show.
      </span>
      <span v-else class="tw:text-zinc-500">
        Review all submitted questions and answers.
      </span>
    </div>
    <Accordion
      v-if="qas && qas.length"
      class="tw:my-4"
      @update:value="handleClickQA($event as string)"
    >
      <AccordionPanel v-for="q in qas" :key="q.id" :value="q.id">
        <AccordionHeader>
          <div class="tw:text-base" v-html="q.question" />
        </AccordionHeader>
        <AccordionContent>
          <div class="tw:pt-2">
            <div
              v-html="q.answer"
              class="tw:text-base tw:border-l-4 tw:border-zinc-500/50 tw:px-4"
            />
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
